import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Landmark, ShieldCheck, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '../../components/public/Footer';

export default function AssetManagement() {
  const [lang, setLang] = useState('En');

  useEffect(() => {
    const syncLang = () => {
      setLang((localStorage.getItem('januslang') || localStorage.getItem('janus_lang')) === 'Fr' ? 'Fr' : 'En');
    };

    syncLang();
    window.addEventListener('langchange', syncLang);
    window.addEventListener('januslangchanged', syncLang);

    return () => {
      window.removeEventListener('langchange', syncLang);
      window.removeEventListener('januslangchanged', syncLang);
    };
  }, []);

  const translations = {
    Fr: {
      metaTitle: "Asset Management | Janus Capital Strategy",
      metaDesc: "Janus Capital Strategy propose une approche patrimoniale de l'asset management fondée sur la cohérence, l'allocation d'actifs, la diversification et la structuration internationale du capital.",
      title: "L'asset management, au service de la cohérence patrimoniale.",
      sub: "Au-delà de la performance isolée, nous aidons à organiser le capital selon ses fonctions : stabilité, rendement, liquidité, diversification et protection.",
      p1: "Chez Janus Capital Strategy, l'asset management ne se résume pas à sélectionner des actifs. Il consiste à organiser un patrimoine de manière lisible, disciplinée et cohérente avec la réalité de vie de l'investisseur. Chaque actif a une fonction : l'immobilier n'a pas le même rôle que le cash, le cash n'a pas le même rôle que les métaux précieux, et les actifs digitaux sélectionnés n'ont pas le même rôle que des actifs de rendement traditionnels. Notre travail consiste à rendre cette architecture intelligible et exploitable.",
      p2: "Nous privilégions une lecture par fonction, et non par narration de marché. Cela signifie que chaque poche du patrimoine est pensée pour ce qu'elle doit réellement accomplir : préserver, générer, diversifier, protéger, ou conserver de la flexibilité. Beaucoup d'investisseurs accumulent des actifs sans construire de logique d'ensemble ; à l'inverse, une bonne allocation améliore la lisibilité, la discipline et la capacité à agir avec cohérence dans le temps.",
      gridTitle: "Ce que couvre notre lecture d'allocation",
      cards: [
        {
          title: "Structure globale du patrimoine",
          text: "Nous cartographions la structure globale de votre patrimoine, la hiérarchie de vos objectifs et l'intégration des actifs tangibles et liquides, dans une lecture internationale de la détention et de la mobilité du capital.",
          icon: <Landmark size={24} />
        },
        {
          title: "Une allocation pensée par fonction",
          text: "Chaque poche de votre patrimoine est pensée pour ce qu'elle doit réellement accomplir : préserver, générer, diversifier ou protéger. Nous arbitrons entre sécurité, croissance, liquidité et revenu selon vos objectifs réels.",
          icon: <LineChart size={24} />
        },
        {
          title: "Livrables Concrets",
          text: "Un audit de portefeuille existant, une proposition d'allocation cible, la sélection de véhicules — immobilier, comptes titres, métaux précieux — et un suivi trimestriel de l'exécution.",
          icon: <ShieldCheck size={24} />
        }
      ],
      cta: "Échanger sur votre structure patrimoniale",
      ctaSecondary: "Voir le Portefeuille"
    },
    En: {
      metaTitle: "Asset Management | Janus Capital Strategy",
      metaDesc: "Janus Capital Strategy offers a wealth-oriented approach to asset management, built on coherence, asset allocation, diversification and international capital structuring.",
      title: "Asset management, in service of wealth coherence.",
      sub: "Beyond isolated performance, we help organize capital according to its real functions: stability, yield, liquidity, diversification and protection.",
      p1: "At Janus Capital Strategy, asset management is not simply about selecting assets. It is about organizing wealth in a way that is legible, disciplined and coherent with the investor's real life situation. Every asset has a function: real estate does not play the same role as cash, cash does not play the same role as precious metals, and selected digital assets do not play the same role as traditional yield assets. Our work is to make this architecture intelligible and actionable.",
      p2: "We favor a function-based reading rather than a market-narrative approach. Each portion of your wealth is designed around what it must actually achieve: preserve, generate, diversify, protect, or maintain flexibility. Many investors accumulate assets without an overall logic; a sound allocation instead improves legibility, discipline and the ability to act coherently over time.",
      gridTitle: "What our allocation reading covers",
      cards: [
        {
          title: "Overall wealth structure",
          text: "We map your overall wealth structure, the hierarchy of your objectives, and the integration of tangible and liquid assets, within an international reading of ownership and capital mobility.",
          icon: <Landmark size={24} />
        },
        {
          title: "Allocation designed by function",
          text: "Each portion of your wealth is designed for what it must actually achieve: preserve, generate, diversify or protect. We balance security, growth, liquidity and income according to your real objectives.",
          icon: <LineChart size={24} />
        },
        {
          title: "Concrete Deliverables",
          text: "A portfolio audit, a target allocation proposal, selection of vehicles — including real estate, securities accounts and precious metals — and quarterly follow-up on execution.",
          icon: <ShieldCheck size={24} />
        }
      ],
      cta: "Discuss your wealth structure",
      ctaSecondary: "View Portfolio"
    },
  };

  const t = translations[lang] || translations.En;
  const isRtl = false;

  return (
    <div className="janus-page font-sans selection:bg-[var(--color-gold)] selection:text-[var(--color-surface)]" dir={isRtl ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{t.metaTitle}</title>
        <meta name="description" content={t.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t.metaTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <meta property="og:url" content="https://januscapitalstrategy.com/asset-management" />
        <meta property="og:image" content="https://januscapitalstrategy.com/uploads/images/Image_ASSET_MANAGEMENT.webp" />
        <meta property="og:site_name" content="Janus Capital" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.metaTitle} />
        <meta name="twitter:description" content={t.metaDesc} />
        <meta name="twitter:image" content="https://januscapitalstrategy.com/uploads/images/Image_ASSET_MANAGEMENT.webp" />
      </Helmet>

      <section className="relative pt-48 pb-32 px-6 lg:px-12 max-w-[1400px] mx-auto text-center border-b border-[var(--color-line)] flex flex-col items-center">
        <h1 className="text-4xl md:text-6xl font-light tracking-wide mb-6 janus-title uppercase">{t.title}</h1>
        <p className="text-[var(--color-gold)] tracking-[0.2em] uppercase text-sm md:text-base font-bold mb-4 text-center max-w-4xl leading-relaxed">{t.sub}</p>
        <img src="https://januscapitalstrategy.com/uploads/images/1784356715968-logo-unifie.webp" alt="Janus Arche" className="h-16 md:h-20 w-auto mt-0 object-contain" />
      </section>

      <section className="py-32 px-6 lg:px-12 max-w-[1200px] mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.p1}</p>
            <p className="janus-text-muted font-light leading-relaxed text-lg text-justify">{t.p2}</p>
          </div>
          <div className="w-full aspect-[4/3] sm:aspect-[16/10] relative rounded-sm overflow-hidden shadow-xl border border-[var(--color-line)] janus-surface">
            <img
              src="https://januscapitalstrategy.com/uploads/images/Image_ASSET_MANAGEMENT.webp"
              alt="Premium Executive Luxury Detail"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="py-32 bg-transparent border-y border-[var(--color-line)] px-6 lg:px-12">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-light text-center text-[var(--color-gold)] uppercase tracking-widest mb-16">{t.gridTitle}</h2>
          <div className="grid md:grid-cols-3 gap-8 items-stretch">
            {t.cards.map((card, idx) => (
              <div
                key={idx}
                className="p-8 border border-[var(--color-line)] janus-surface hover:border-[var(--color-gold)] hover:bg-[var(--color-gold)] transition-all duration-500 group flex flex-col shadow-sm hover:shadow-xl rounded-sm"
              >
                <div className="text-[var(--color-line)] group-hover:text-white/20 transition-colors duration-500 mb-6">
                  {card.icon}
                </div>
                <h3 className="text-lg font-bold tracking-wide uppercase mb-4 janus-title group-hover:text-white transition-colors duration-500">
                  {card.title}
                </h3>
                <p className="janus-text-muted text-sm font-light leading-relaxed group-hover:text-white/90 transition-colors duration-500 flex-grow">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('openContactModal', { detail: { source: 'asset' } }));
                }}
                className="janus-cta-secondary !border-2 !border-[var(--color-black)] hover:!bg-[var(--color-black)] hover:!text-white shadow-xl !px-12 !py-5"
              >
                {t.cta}
              </button>
              <Link to="/portfolio" className="inline-block border border-[var(--color-line)] text-[var(--color-black)] px-12 py-5 text-[10px] font-bold tracking-[0.2em] uppercase hover:border-[var(--color-gold)] transition">
                {t.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
